import { useEffect } from 'react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import * as Sentry from '@sentry/react';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';
import { getTokenLockTime } from '@dealstack/frontend/shared/hooks/usePeriodicRefresh/periodRefeshUtils';
import { isReportableApiFailure } from '@dealstack/frontend/shared/dataExchange/ApiLayer/reportableFailure';
import { isBeforeCurrentTime } from '@dealstack/frontend/shared/helper/timeHelpers';
import { DateTime } from 'luxon';
import type { UserState } from '@dealstack/frontend/shared/mechanism/redux/reducers/user/user';
import {
	BACKGROUND_REQUEST_HEADER,
	BACKGROUND_REQUEST_HEADER_VALUE,
} from '@dealstack/shared/lib/common/session/sessionContract';

// It must match the value from the workos dashboard - authentication - session
const WORKOS_TOKEN_DURATION_IN_MS = 5 * 60 * 1000;
export const REFRESH_INTERVAL = WORKOS_TOKEN_DURATION_IN_MS - 30 * 1000; // having a 30 seconds delta to ensure that the latency network has no impact

const refreshToken = async (): Promise<void> => {
	const params = {
		method: HTTP_METHOD.POST,
		endpoint: API_PATHS.AUTH_REFRESH_TOKEN_POST,
		// Always background: fires on a timer whether or not anyone is there.
		customHeaders: {
			[BACKGROUND_REQUEST_HEADER]: BACKGROUND_REQUEST_HEADER_VALUE,
		},
	};
	await makeRequest(params);
};

const handleTokenRefresh = async (): Promise<void> => {
	const tokenLockTime = getTokenLockTime();

	// Check if the token lock time is still valid
	if (tokenLockTime && isBeforeCurrentTime(tokenLockTime)) {
		return;
	}

	try {
		// Set the new token lock time
		const lockTime = DateTime.now()
			.plus({ milliseconds: REFRESH_INTERVAL })
			.toMillis()
			.toString();

		StorageUtilities.setItem(StorageKeys.TOKEN_LOCK_TIME_IN_Millis, lockTime);

		// Refresh the token
		await refreshToken();
	} catch (err) {
		// A session that has simply ended, or a transport failure already reported
		// under its own fingerprint, is not worth a second event here.
		if (isReportableApiFailure(err)) {
			Sentry.captureException(err);
		}
		// eslint-disable-next-line no-console
		console.error('Token refresh failed', err);

		// Remove the token lock if an error occurred
		StorageUtilities.removeItem(StorageKeys.TOKEN_LOCK_TIME_IN_Millis);
	}
};

export const usePeriodicRefresh = (userState: UserState): void => {
	useEffect(() => {
		// We should return early if userState is still loading or contains no data, as this indicates that the user is logged out if there is no user data. This prevents refetching new tokens when the user is not authenticated
		if (userState.isLoading || !userState.data) {
			return;
		}

		const intervalId = setInterval(async () => {
			await handleTokenRefresh();
		}, REFRESH_INTERVAL);
		return () => clearInterval(intervalId);
	}, [userState.isLoading, userState.data, REFRESH_INTERVAL]);
};
